import { Layout } from 'components'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { createNewUser, checkINN } from 'service/AdminService'
import {
    postRegisterUser,
    setPositionById,
} from 'store/slices/AdminSlice'
import Notification from 'utils/Notifications'
import styles from './RegisterForm.module.scss'
import { ScaleLoader } from 'react-spinners'
import { useEffect } from 'react'
import { getCurrentDate } from 'utils/todayDateForInput'

const RegisterForm = () => {
  const [loader, setLoader] = useState(false)
  const [first_name, setFirst_name] = useState('')
  const [last_name, setLast_name] = useState('')
  const [middle_name, setMiddle_name] = useState('')
  const [number_phone, setNumber_phone] = useState('')
  const [inn, setInn] = useState(null)
  
  // Состояния для проверки ИНН
  const [checkInn, setCheckInn] = useState('')
  const [innCheckResult, setInnCheckResult] = useState(null)
  const [innCheckLoading, setInnCheckLoading] = useState(false)

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  const [notify, setNotify] = useState({
    isOpen: false,
    message: '',
    type: '',
  })
  const dispatch = useDispatch()

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const validatePhone = (phone) => {
    return phone.length >= 10 && /^\d+$/.test(phone)
  }

  const validateINN = (inn) => {
    return inn && inn.toString().length >= 10 && /^\d+$/.test(inn)
  }

  const validateField = (name, value) => {
    let error = ''
    switch (name) {
      case 'first_name':
      case 'last_name':

        if (!value || value.trim() === '') error = 'Обязательное поле'
        break
      case 'number_phone':
        if (!value) error = 'Обязательное поле'
        else if (!validatePhone(value)) error = 'Минимум 10 цифр'
        break
      case 'inn':
        if (!value) error = 'Обязательное поле'
        else if (!validateINN(value)) error = 'Минимум 10 цифр'
        break
      default:
        break
    }
    return error
  }

  const handleBlur = (name, value) => {
    setTouched({ ...touched, [name]: true })
    const error = validateField(name, value)
    setErrors({ ...errors, [name]: error })
  }

  // Функция проверки ИНН
  const handleCheckINN = async () => {
    if (!checkInn || checkInn.length < 10) {
      setNotify({
        isOpen: true,
        message: 'Введите корректный ИНН (минимум 10 цифр)',
        type: 'warning',
        sound: 'warning',
      })
      return
    }

    try {
      setInnCheckLoading(true)
      const response = await checkINN(checkInn)
      setInnCheckResult(response.data)
    } catch (error) {
      console.error('Ошибка проверки ИНН:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при проверке ИНН',
        type: 'error',
        sound: 'error',
      })
    } finally {
      setInnCheckLoading(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    // Validate all fields
    const newErrors = {
      first_name: validateField('first_name', first_name),
      last_name: validateField('last_name', last_name),
      middle_name: validateField('middle_name', middle_name),
      number_phone: validateField('number_phone', number_phone),
      inn: validateField('inn', inn),
    }

    setErrors(newErrors)
    setTouched({
      first_name: true,
      last_name: true,
      middle_name: true,
      number_phone: true,
      inn: true,
    })

    const hasErrors = Object.values(newErrors).some(error => error !== '')

    if (hasErrors) {
      setNotify({
        isOpen: true,
        message: 'Пожалуйста, исправьте ошибки в форме',
        type: 'warning',
        sound: 'warning',
      })
      return
    }

    try {
      setLoader(true)
      const password = inn
      let response = await createNewUser(
        first_name,
        last_name,
        middle_name,
        number_phone,
        inn,
        password,
      )
      dispatch(
        postRegisterUser({
          first_name,
          last_name,
          middle_name,
          number_phone,
          inn,
          password,
        }),
      )
      setNotify({
        isOpen: true,
        message: 'Пользователь успешно зарегистрирован!',
        type: 'success',
        sound: 'success',
      })
      
      // Clear form fields
      setFirst_name('')
      setLast_name('')
      setMiddle_name('')
      setNumber_phone('')
      setInn(null)
      setErrors({})
      setTouched({})
    } catch (error) {
      console.error('Registration error:', error)
      setNotify({
        isOpen: true,
        message: error.response?.data?.message || 'Ошибка регистрации. Попробуйте снова.',
        type: 'error',
        sound: 'error',
      })
    } finally {
      setLoader(false)
    }
  }

  //Consts & Let

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>
            <span className={styles.titleIcon}>👤</span>
            Регистрация пользователя
          </div>
          <p className={styles.subtitle}>Заполните все поля для создания нового пользователя</p>
        </div>

        {/* Проверка ИНН */}
        <div className={styles.innCheckSection}>
          <label className={styles.sectionLabel}>Проверка ИНН в базе данных</label>
          <div className={styles.innCheckWrapper}>
            <input
              type="text"
              className={styles.input}
              placeholder="Введите ИНН для проверки"
              value={checkInn}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                setCheckInn(value)
                setInnCheckResult(null)
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCheckINN()
                }
              }}
              maxLength={14}
            />
            <button
              type="button"
              className={styles.checkButton}
              onClick={handleCheckINN}
              disabled={innCheckLoading}
            >
              {innCheckLoading ? 'Проверка...' : 'Проверить'}
            </button>
            {innCheckResult && (
              <span
                className={`${styles.innResult} ${
                  innCheckResult.exists ? styles.innExists : styles.innNotExists
                }`}
              >
                {innCheckResult.message}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Personal Information */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Личные данные</label>
            <div className={styles.inputGrid}>
              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel}>Фамилия *</label>
                <input
                  type="text"
                  className={`${styles.input} ${touched.last_name && errors.last_name ? styles.inputError : ''}`}
                  placeholder="Иванов"
                  value={last_name}
                  onChange={(e) => setLast_name(e.target.value)}
                  onBlur={() => handleBlur('last_name', last_name)}
                />
                {touched.last_name && errors.last_name && (
                  <span className={styles.errorText}>{errors.last_name}</span>
                )}
              </div>

              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel}>Имя *</label>
                <input
                  type="text"
                  className={`${styles.input} ${touched.first_name && errors.first_name ? styles.inputError : ''}`}
                  placeholder="Иван"
                  value={first_name}
                  onChange={(e) => setFirst_name(e.target.value)}
                  onBlur={() => handleBlur('first_name', first_name)}
                />
                {touched.first_name && errors.first_name && (
                  <span className={styles.errorText}>{errors.first_name}</span>
                )}
              </div>

              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel}>Отчество</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Иванович"
                  value={middle_name}
                  onChange={(e) => setMiddle_name(e.target.value)}
                  onBlur={() => handleBlur('middle_name', middle_name)}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Контактная информация</label>
            <div className={styles.inputGrid}>
              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel}>Номер телефона *</label>
                <input
                  type="tel"
                  className={`${styles.input} ${touched.number_phone && errors.number_phone ? styles.inputError : ''}`}
                  placeholder="+996 XXX XXX XXX"
                  value={number_phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    setNumber_phone(value)
                  }}
                  onBlur={() => handleBlur('number_phone', number_phone)}
                />
                {touched.number_phone && errors.number_phone && (
                  <span className={styles.errorText}>{errors.number_phone}</span>
                )}
              </div>
            </div>
          </div>

          {/* Tax Information */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Налоговая информация</label>
            <div className={styles.inputGrid}>
              <div className={styles.inputWrapper}>
                <label className={styles.inputLabel}>ИНН *</label>
                <input
                  type="text"
                  className={`${styles.input} ${touched.inn && errors.inn ? styles.inputError : ''}`}
                  placeholder="Введите ИНН"
                  value={inn || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    setInn(value)
                  }}
                  onBlur={() => handleBlur('inn', inn)}
                />
                {touched.inn && errors.inn && (
                  <span className={styles.errorText}>{errors.inn}</span>
                )}
                <span className={styles.helperText}>ИНН будет использован как пароль</span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className={styles.submitSection}>
            {loader ? (
              <div className={styles.loaderWrapper}>
                <ScaleLoader color="#736ced" height={35} width={4} />
                <span className={styles.loaderText}>Регистрация...</span>
              </div>
            ) : (
              <button type="submit" className={styles.submitButton}>
                <span className={styles.submitIcon}>✓</span>
                Зарегистрировать пользователя
              </button>
            )}
          </div>
        </form>
      </div>
      <Notification notify={notify} setNotify={setNotify} />
    </Layout>
  )
}

export default RegisterForm
